import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Buyer's Inspection", "Pre-Listing Inspection", "Roof & Aerial Survey", "Infrared & Moisture", "Not Sure Yet"];

const empty = { full_name: "", email: "", phone: "", property_address: "", service: "Buyer's Inspection", message: "" };

export default function BookingForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.property_address) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Request received. We'll be in touch same-day.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-0 border-b border-white/20 focus:border-gold outline-none py-3 text-cream placeholder:text-cream/30 transition-colors duration-300 font-sans";
  const labelCls = "font-display uppercase tracking-[0.18em] text-[11px] text-gold/80 mb-1 block";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="border border-gold/40 bg-surface p-12 text-center"
        data-testid="booking-success"
      >
        <div className="mx-auto w-16 h-16 rounded-full border border-gold flex items-center justify-center mb-6">
          <Check className="text-gold" size={30} />
        </div>
        <h3 className="font-display uppercase text-cream text-3xl tracking-tight">Request Received</h3>
        <p className="text-cream/60 mt-3 leading-relaxed max-w-md mx-auto">
          Thank you. A member of the I.D. Home Inspections team will reach out to you same-day to confirm
          your luxury estate inspection.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-8 border border-gold/60 text-gold hover:bg-gold hover:text-ink transition-colors px-8 py-3 font-display uppercase tracking-[0.15em] text-xs"
          data-testid="booking-new-request"
        >
          Submit Another Request
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input className={field} value={form.full_name} onChange={set("full_name")} placeholder="Jane Whitfield" data-testid="booking-name" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" className={field} value={form.email} onChange={set("email")} placeholder="jane@email.com" data-testid="booking-email" />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input className={field} value={form.phone} onChange={set("phone")} placeholder="(470) 555-0142" data-testid="booking-phone" />
        </div>
        <div>
          <label className={labelCls}>Service *</label>
          <select className={`${field} appearance-none cursor-pointer`} value={form.service} onChange={set("service")} data-testid="booking-service">
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-surface text-cream">{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Property Address *</label>
        <input className={field} value={form.property_address} onChange={set("property_address")} placeholder="1200 Estate Ridge, Cumming, GA" data-testid="booking-address" />
      </div>

      <div>
        <label className={labelCls}>Notes</label>
        <textarea rows={3} className={field} value={form.message} onChange={set("message")} placeholder="Tell us about the property or any concerns..." data-testid="booking-message" />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="group inline-flex items-center gap-3 bg-gold text-ink px-10 py-4 font-display uppercase tracking-[0.15em] text-sm hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60"
        data-testid="booking-submit"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : null}
        {loading ? "Sending..." : "Request Inspection"}
      </button>
    </form>
  );
}
